// Slide-over panel: product gallery, description, variants, tags, status, link.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ExternalLink, Loader2, Tag as TagIcon, Layers, Package, ImageOff, History,
} from "lucide-react";
import { getProduct, updateProduct } from "@/lib/products";
import { fmtDate, fmtRelative } from "@/lib/format";

export default function ProductSidebar({ open, productId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    if (!open || !productId) return;
    setLoading(true);
    setActiveImg(0);
    getProduct(productId)
      .then(setData)
      .catch(() => toast.error("Eroare la încărcarea produsului"))
      .finally(() => setLoading(false));
  }, [open, productId]);

  const product = data?.product;
  const images = product?.images?.length ? product.images : (product?.featured_image ? [product.featured_image] : []);

  const toggleStatus = async () => {
    if (!product) return;
    const newStatus = product.status === "ACTIVE" ? "DRAFT" : "ACTIVE";
    const tid = toast.loading(`Setez status ${newStatus.toLowerCase()}...`);
    try {
      const r = await updateProduct(product.id, { status: newStatus });
      if (r.ok && r.product) {
        setData({ ...data, product: r.product });
        toast.success(`Status actualizat → ${newStatus}`, { id: tid });
      } else {
        toast.error(r.errors?.[0] || "Eroare", { id: tid });
      }
    } catch (e) {
      toast.error(e?.message || "Eroare", { id: tid });
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:w-[560px] sm:max-w-[560px] p-0 overflow-y-auto"
        data-testid="product-sidebar"
      >
        {loading || !product ? (
          <div className="h-full flex items-center justify-center">
            <VisuallyHidden><SheetTitle>Detalii produs</SheetTitle></VisuallyHidden>
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col">
            {/* HEADER */}
            <SheetHeader className="px-6 py-4 border-b border-border">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <SheetTitle className="text-base font-display tracking-tight pr-8">
                    {product.title}
                  </SheetTitle>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                    {product.vendor && <span>{product.vendor}</span>}
                    {product.product_type && <span>· {product.product_type}</span>}
                    {product.shopify_updated_at && (
                      <span>· actualizat {fmtRelative(product.shopify_updated_at)}</span>
                    )}
                  </div>
                </div>
              </div>
            </SheetHeader>

            <div className="px-6 py-5 space-y-6">
              {/* Quick actions */}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleStatus}
                  data-testid="product-toggle-status"
                >
                  {product.status === "ACTIVE" ? "Mută în Draft" : "Activează"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  asChild
                  data-testid="product-open-shopify"
                >
                  <a
                    href={`https://ieftinit-2841.myshopify.com/admin/products/${product.shopify_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Shopify Admin
                  </a>
                </Button>
              </div>

              {/* GALLERY */}
              {images.length > 0 ? (
                <Section title="Imagini" icon={Package}>
                  <div className="space-y-2">
                    <div className="aspect-square w-full rounded-lg bg-secondary overflow-hidden">
                      <img
                        src={images[activeImg]}
                        alt={product.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    {images.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {images.map((img, i) => (
                          <button
                            key={i}
                            onClick={() => setActiveImg(i)}
                            className={`h-14 w-14 rounded-md overflow-hidden shrink-0 ring-2 ${
                              i === activeImg ? "ring-primary" : "ring-transparent hover:ring-border"
                            }`}
                          >
                            <img src={img} alt="" className="h-full w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Section>
              ) : (
                <Section title="Imagini" icon={Package}>
                  <div className="aspect-square w-full rounded-lg bg-secondary flex items-center justify-center text-muted-foreground">
                    <ImageOff className="h-8 w-8" />
                  </div>
                </Section>
              )}

              {/* STATUS / STOCK */}
              <Section title="Stoc & Status">
                <div className="grid grid-cols-3 gap-3">
                  <KV label="Status" value={
                    <Badge variant="outline" className="capitalize text-[10px]">
                      {product.status?.toLowerCase()}
                    </Badge>
                  } />
                  <KV label="Stoc total" value={product.total_inventory} mono />
                  <KV label="Variante" value={product.variants_count} mono />
                </div>
              </Section>

              {/* VARIANTS */}
              <Section title={`Variante (${product.variants_count})`} icon={Layers}>
                <div className="space-y-2">
                  {(product.variants || []).map((v) => (
                    <div key={v.variant_id} className="rounded-md bg-secondary/30 p-3 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium truncate">{v.title || "Default"}</div>
                        <div className="text-xs text-muted-foreground tabular-nums">
                          Stoc: <span className="font-medium text-foreground">{v.inventory_quantity}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-[11px]">
                        <KV label="SKU" value={v.sku} mono />
                        <KV label="Barcode" value={v.barcode} mono />
                        <KV label="Preț" value={v.price ? `${v.price} RON` : "—"} mono />
                        <KV label="Cost" value={v.cost ? `${v.cost} RON` : "—"} mono />
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* TAGS */}
              <Section title="Tag-uri" icon={TagIcon}>
                {(product.tags || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">Niciun tag.</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {product.tags.map((t, i) => (
                      <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>
                    ))}
                  </div>
                )}
              </Section>

              {/* DESCRIPTION */}
              {product.description_html && (
                <Section title="Descriere">
                  <div
                    className="prose prose-sm max-w-none text-sm text-muted-foreground [&_*]:!text-muted-foreground"
                    dangerouslySetInnerHTML={{ __html: product.description_html }}
                  />
                </Section>
              )}

              {/* CHANGES HISTORY */}
              {data?.changes?.length > 0 && (
                <Section title="Modificări recente" icon={History}>
                  <div className="space-y-1.5">
                    {data.changes.slice(0, 10).map((c) => (
                      <div key={c.id} className="text-xs flex items-center justify-between gap-2 py-1 border-b border-border/40 last:border-0">
                        <div className="min-w-0">
                          <span className="text-muted-foreground capitalize">{c.field}:</span>{" "}
                          <span className="font-mono text-foreground">
                            {c.old_value || "—"} → {c.new_value || "—"}
                          </span>
                        </div>
                        <div className="text-[10px] text-muted-foreground shrink-0">
                          {c.actor_name?.split("@")[0]} · {fmtDate(c.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}


function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />}
        {title}
      </div>
      {children}
    </section>
  );
}


function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold">{label}</div>
      <div className={`text-sm mt-0.5 ${mono ? "font-mono" : ""}`}>
        {value || value === 0 ? value : <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}
